import { useState, useEffect } from 'react';
import { getLeaderboard } from '../../api/clubs';
import type { LeaderboardEntryResponse } from '../../types/api';

type LeaderboardType = 'TotalCatches' | 'BiggestFish' | 'SpeciesVariety' | 'MonthlyCatches';

const TABS: { type: LeaderboardType; label: string }[] = [
  { type: 'TotalCatches', label: 'Total' },
  { type: 'BiggestFish', label: 'Biggest' },
  { type: 'SpeciesVariety', label: 'Species' },
  { type: 'MonthlyCatches', label: 'Monthly' },
];

function valueLabel(type: LeaderboardType): string {
  switch (type) {
    case 'TotalCatches': return 'catches';
    case 'BiggestFish': return 'in';
    case 'SpeciesVariety': return 'species';
    case 'MonthlyCatches': return 'this month';
  }
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return (
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-xs font-bold text-amber-600 dark:text-amber-400 shrink-0">
      1
    </span>
  );
  if (rank === 2) return (
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 text-xs font-bold text-slate-500 dark:text-slate-300 shrink-0">
      2
    </span>
  );
  if (rank === 3) return (
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-200 dark:bg-orange-900/40 text-xs font-bold text-orange-700 dark:text-orange-500 shrink-0">
      3
    </span>
  );
  return (
    <span className="flex items-center justify-center w-6 h-6 text-xs text-slate-400 dark:text-slate-500 shrink-0">
      {rank}
    </span>
  );
}

interface Props {
  clubId: number;
}

export function LeaderboardCard({ clubId }: Props) {
  const [activeTab, setActiveTab] = useState<LeaderboardType>('TotalCatches');
  const [entries, setEntries] = useState<LeaderboardEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getLeaderboard(clubId, activeTab)
      .then((data) => setEntries(data.entries))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load leaderboard'))
      .finally(() => setLoading(false));
  }, [clubId, activeTab]);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">Leaderboard</h2>

      <div className="mb-4 flex gap-1 flex-wrap">
        {TABS.map((tab) => (
          <button
            key={tab.type}
            onClick={() => setActiveTab(tab.type)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === tab.type
                ? 'bg-teal-600 dark:bg-teal-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No data yet</p>
      )}

      {!loading && !error && entries.length > 0 && (
        <ol className="space-y-1">
          {entries.map((entry) => (
            <li
              key={entry.userId}
              className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${
                entry.rank === 1
                  ? 'bg-amber-50 dark:bg-amber-900/10'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <RankBadge rank={entry.rank} />
                <span className={`text-sm font-medium ${
                  entry.rank === 1
                    ? 'text-slate-900 dark:text-slate-50'
                    : 'text-slate-700 dark:text-slate-200'
                }`}>
                  {entry.displayName}
                </span>
              </div>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {activeTab === 'BiggestFish' ? entry.value.toFixed(1) : entry.value}{' '}
                {valueLabel(activeTab)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
